import { createAsyncThunk } from "@reduxjs/toolkit";
import { EndPoints } from "../../api/endpoints";
import axiosClient from "../../api/axiosClient";
import { Exam } from "../apiSlice/examSlice";

type FilterQueryParams = {
  from: number;
  to: number;
  atc_id: string;
};

export type ISendQuote = {
  request_id: string;
  amount: number;
};

export type IDenyRequest = {
  request_id: string;
  reason: string;
};
export const getRequestsAtc = createAsyncThunk(
  "get/requestsAtc",
  async (_, { rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.get(EndPoints.requestsAtc);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const getRequestsByAtc = createAsyncThunk(
  "getRequestsByAtc",
  async (
    { atc_id, filter }: { atc_id: string; filter: string },
    { rejectWithValue }
  ): Promise<any> => {
    try {
      const response = await axiosClient.get(
        `requests/atc/${atc_id}/voucher${filter}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);
export const getQuoteByAtc = createAsyncThunk(
  "getQuoteByAtc",
  async (
    { atc_id, filter }: { atc_id: string; filter: string },
    { rejectWithValue }
  ): Promise<any> => {
    try {
      const response = await axiosClient.get(
        `requests/atc/${atc_id}/quote${filter}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const denyRequest = createAsyncThunk(
  "denyRequest",
  async (data: IDenyRequest, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.post(
        `requests/${data.request_id}/deny`,
        {
          reason: data.reason,
        }
      );
      dispatch(getRequestsAtc());
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const atcRequestVoucher = createAsyncThunk(
  "atcRequestVoucher",
  async (values: any, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.post(
        `requests/atc/${values.atc_id}/voucher`,
        values.data
      );
      dispatch(getRequestsByAtc({ atc_id: values.atc_id, filter: "" }));
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const atcRequestQuote = createAsyncThunk(
  "atcRequestQuote",
  async (values: any, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.post(
        `requests/atc/${values.atc_id}/quote`,
        values.data
      );
      dispatch(getQuoteByAtc({ atc_id: values.atc_id, filter: "" }));
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const voucherFromQuote = createAsyncThunk(
  "voucherFromQuote",
  async (values: any, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.post(
        `requests/atc/${values.atc_id}/voucherFromQuote`,
        values.data
      );
      dispatch(getQuoteByAtc({ atc_id: values.atc_id, filter: "" }));
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const sendQuote = createAsyncThunk(
  "sendQuote",
  async (data: ISendQuote, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.post(
        `requests/${data.request_id}/quote`,
        {
          amount: data.amount,
        }
      );
      dispatch(getRequestsAtc());
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export type ApproveVoucher = {
  otp: string;
  expiry_date: string;
  request_id: string;
};
export const approveVoucher = createAsyncThunk(
  "approveVoucher",
  async (data: ApproveVoucher, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.post(
        `requests/${data.request_id}/voucher`,
        {
          otp: data.otp,
          expiry_date: data.expiry_date,
        }
      );
      dispatch(getRequestsAtc());
      return response.data;
    } catch (error: any) {
      // Lead with the backend's reason ("Invalid OTP") — the axios fallback is
      // "Request failed with status code 403", which tells the proctor nothing.
      return rejectWithValue({
        message: error?.response?.data?.status ?? error.message,
      });
    }
  }
);

export const getRequestsUser = createAsyncThunk(
  "get/requestsUser",
  async (_, { rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.get(EndPoints.requestsUser);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const deleteExam = createAsyncThunk(
  "delete/requests",
  async (id: string, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.delete(
        `${EndPoints.requestsAtc}/${id}`
      );
      dispatch(getRequestsAtc());
      return { status: response.data.status, id };
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const createExam = createAsyncThunk(
  "create/requests",
  async (data: Exam, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.put(`${EndPoints.requestsAtc}`, data);
      dispatch(getRequestsAtc());
      return { status: response.data.status };
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const updateExam = createAsyncThunk(
  "update/requests",
  async (data: Exam, { dispatch, rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.patch(
        `${EndPoints.requestsAtc}/${data.id}`,
        data
      );
      dispatch(getRequestsAtc());
      return { status: response.data.status };
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const filterRequests = createAsyncThunk(
  "filterRequests",
  async (queryParams: FilterQueryParams, { rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.get(
        `${EndPoints.requestsAtc}/filter/?from=${queryParams.from}&to=${queryParams.to}&atc_id=${queryParams.atc_id}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);

export const filterUserRequests = createAsyncThunk(
  "filterUserRequests",
  async (queryParams: FilterQueryParams, { rejectWithValue }): Promise<any> => {
    try {
      const response = await axiosClient.get(
        `${EndPoints.requestsUser}/filter?from=${queryParams.from}&to=${queryParams.to}&exam_id=${queryParams.atc_id}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        message: error.message,
        description: error?.response?.data?.status,
      });
    }
  }
);
